import type { DocumentReference } from "@medplum/fhirtypes";
import {
  buildMobileDocumentSessionFileId,
  MobileDocumentIdentifierSystem,
  MobileDocumentOriginValue,
} from "./mobile-upload-contract";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const BINARY_ID_REGEX = /Binary\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;
const ANY_UUID_IN_STRING_REGEX = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

/**
 * Returns true when the value matches the UUID format Medplum uses for Binary ids.
 */
function isUuid({ value }: { value: string }): boolean {
  return UUID_REGEX.test(value);
}

/**
 * Resolves the identifier value for a shared mobile-upload document marker.
 */
export function getMobileDocumentIdentifierValue({
  document,
  system,
}: {
  document: DocumentReference;
  system: string;
}): string | undefined {
  return document.identifier?.find((identifier) => identifier.system === system)?.value;
}

/**
 * Returns the durable session/local-file binding stored on a mobile upload.
 */
export function getMobileDocumentSessionBinding({
  document,
}: {
  document: DocumentReference;
}): { sessionId: string; localFileId: string } | undefined {
  const sessionId = getMobileDocumentIdentifierValue({
    document,
    system: MobileDocumentIdentifierSystem.SessionId,
  });
  const localFileId = getMobileDocumentIdentifierValue({
    document,
    system: MobileDocumentIdentifierSystem.LocalFileId,
  });

  if (!sessionId || !localFileId) {
    return undefined;
  }

  return { sessionId, localFileId };
}

/**
 * Attempts to resolve a Binary id from a direct reference or URL-shaped attachment value.
 */
export function resolveBinaryIdFromUrl(url?: string): string | undefined {
  if (!url || typeof url !== "string") {
    return undefined;
  }

  const directBinaryRef = url.match(/^Binary\/([0-9a-f-]{36})$/i);
  if (directBinaryRef?.[1] && isUuid({ value: directBinaryRef[1] })) {
    return directBinaryRef[1];
  }

  const embeddedBinaryRef = url.match(BINARY_ID_REGEX);
  if (embeddedBinaryRef?.[1] && isUuid({ value: embeddedBinaryRef[1] })) {
    return embeddedBinaryRef[1];
  }

  // Presigned URLs sometimes drop the `Binary/` segment but still embed the same UUID.
  const looksPresigned =
    url.includes("X-Amz-Algorithm=") ||
    url.includes("X-Amz-Signature=") ||
    url.includes("Signature=");
  if (!looksPresigned) {
    return undefined;
  }

  const anyUuid = url.match(ANY_UUID_IN_STRING_REGEX);
  if (anyUuid?.[1] && isUuid({ value: anyUuid[1] })) {
    return anyUuid[1];
  }

  return undefined;
}

/**
 * Returns true when a document is still bound to the requested mobile upload handle.
 */
export function doesMobileDocumentMatchRequestedUpload({
  document,
  sessionId,
  localFileId,
}: {
  document: DocumentReference;
  sessionId: string;
  localFileId: string;
}): boolean {
  const expectedSessionFileId = buildMobileDocumentSessionFileId({
    sessionId,
    localFileId,
  });
  const sessionFileId = getMobileDocumentIdentifierValue({
    document,
    system: MobileDocumentIdentifierSystem.SessionFileId,
  });
  const documentSessionId = getMobileDocumentIdentifierValue({
    document,
    system: MobileDocumentIdentifierSystem.SessionId,
  });
  const documentLocalFileId = getMobileDocumentIdentifierValue({
    document,
    system: MobileDocumentIdentifierSystem.LocalFileId,
  });

  if (sessionFileId && sessionFileId !== expectedSessionFileId) {
    return false;
  }

  if (documentSessionId && documentSessionId !== sessionId) {
    return false;
  }

  if (documentLocalFileId && documentLocalFileId !== localFileId) {
    return false;
  }

  if (sessionFileId === expectedSessionFileId) {
    return true;
  }

  return documentSessionId === sessionId && documentLocalFileId === localFileId;
}

/**
 * Returns true when a document still carries the expected file fingerprint.
 */
export function doesMobileDocumentMatchFileFingerprint({
  document,
  fileFingerprint,
}: {
  document: DocumentReference;
  fileFingerprint: string;
}): boolean {
  return (
    getMobileDocumentIdentifierValue({
      document,
      system: MobileDocumentIdentifierSystem.FileFingerprint,
    }) === fileFingerprint
  );
}

/**
 * Returns true when a document came from a supported mobile upload flow.
 */
export function isMobileUploadOrigin(document: DocumentReference): boolean {
  const origin = getMobileDocumentIdentifierValue({
    document,
    system: MobileDocumentIdentifierSystem.Origin,
  });

  return origin === MobileDocumentOriginValue.Records || origin === MobileDocumentOriginValue.Share;
}
